import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import FontIcon from '@/components/Header/FontIcon';
import { trackEventWithObject } from '@/utils/gaDataPush';
import useStore from '@/store/useStore';
import useMediaQuery from '@/hooks/useMediaQuery';

const MOBILE_NAV_ROOT_ID = 'be-mobile-nav-links-root';

export type MobileHeaderProps = {
  className?: string;
  mobileNavLinks?: unknown[];
  /**
   * Drawer markup pre-rendered by `renderMobileNavShellHtml`. When present the nav is mounted as
   * a static island that React never reconciles, so Dynamic Yield's pre-hydration edits survive
   * instead of being reverted on hydration. Falls back to the React tree when omitted.
   */
  mobileNavHtml?: string;
};

const Container = styled.div.attrs(props => ({
  className: props.className,
}))`
  .shrink-bar {
    height: 0;
    top: -43px;
  }
`;
const MobileNavigation = dynamic(() => import('./MobileNavigation'));
const MobileNavigationIsland = dynamic(() => import('./MobileNavigation').then(mod => mod.MobileNavigationIsland));

export default function MobileHeader({ className = '', mobileNavLinks, mobileNavHtml }: MobileHeaderProps) {
  const [isMemuOpen, setIsMemuOpen] = useState(false);
  const [isTopNavBarHide, setIsTopNavBarHide] = useState(false);
  const [navTop, setNavTop] = useState(0);
  const [phone, setPhone] = useState('');
  const { convenientShowroomFullPhoneNumber, currCountry } = useStore(state => state.user);
  const { store, visitText, telCode, showroomName } = useStore(state => state.showroom);
  const { localGeToggle } = useStore(state => state.glLocation);

  const isGlobalE = localGeToggle !== null && !localGeToggle;

  const isBelowDesktop = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    if (isGlobalE) {
      (window as any).getGlobalEPhoneNumber().then(p =>
        setPhone(() => {
          if (
            currCountry === 'united states' &&
            convenientShowroomFullPhoneNumber &&
            convenientShowroomFullPhoneNumber !== '1.800.691.0952'
          ) {
            return convenientShowroomFullPhoneNumber;
          }

          return p === '800.691.0952' ? '1.800.691.0952' : p;
        })
      );
    } else {
      setPhone(() => {
        if (
          currCountry === 'united states' &&
          convenientShowroomFullPhoneNumber &&
          convenientShowroomFullPhoneNumber !== '1.800.691.0952'
        ) {
          return `${convenientShowroomFullPhoneNumber}`;
        }

        return `${telCode}${store?.tel}`;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalE, store]);

  useEffect(() => {
    let topNavBarRect: DOMRect;

    const handleScroll = () => {
      // Handle navbar height calculation
      topNavBarRect = document.querySelector('.mobile-header-banner-height')?.getBoundingClientRect()!;
      const navHeight = window.scrollY >= topNavBarRect?.height || !topNavBarRect;

      setIsTopNavBarHide(navHeight);
      setNavTop(navHeight ? 46 : 46 + (topNavBarRect?.top || 40));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial calculation for navbar height
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const scrollableElement = document.querySelector('.mm-panel');

    if (!scrollableElement) return; // Ensure the element exists

    const handleScroll = () => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.tagName === 'INPUT') {
        activeElement.blur(); // Blur the input to hide the keyboard
      }
    };

    // Attach scroll event listener to the specific element
    scrollableElement.addEventListener('scroll', handleScroll);

    return () => {
      // Cleanup listener when the component unmounts
      scrollableElement.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const removeSearchspringScript = () => {
    const script = document.querySelector('script[src="https://snapui.searchspring.io/s9f4bl/bundle.js"]');
    if (script) {
      script.remove();
    }
    const autocompleteTarget = document.querySelector('.ss__autocomplete--target');
    if (autocompleteTarget) {
      autocompleteTarget.remove();
    }
  };

  const addSearchspringScript = () => {
    const script = document.createElement('script');
    script.src = 'https://snapui.searchspring.io/s9f4bl/bundle.js';
    script.defer = true;
    document.head.appendChild(script);
  };

  /*
    Remounting SS script because it injects the autosuggest only once, either on desktop or mobile.
  */
  useEffect(() => {
    addSearchspringScript();
    /*
      Deletes stale carousels after 2 seconds to avoid layout shifting.
    */
    setTimeout(() => {
      const carousels = document.querySelectorAll('div.searchspring-container');
      carousels?.forEach(carousel => {
        const injectedElems = carousel.querySelectorAll('& > div');
        injectedElems.forEach((elem, index) => {
          // Remove all but last one
          if (index < injectedElems.length - 1) {
            elem.remove();
          }
        });
      });
    }, 2000);

    return () => {
      removeSearchspringScript();
    };
  }, [isMemuOpen, isBelowDesktop]);

  return (
    <Container className={className}>
      <div
        className={classNames(
          'king-navgation-mobile king-header-wrapper main-navbox-mobile-ab2 mobile-header-banner-height',
          isTopNavBarHide && 'tw-fixed tw-top-0',
          isMemuOpen || isTopNavBarHide ? 'tw-h-[46px]' : 'tw-h-[89px]'
        )}
      >
        <div className="navbar">
          <div className="navbar-header">
            <div className="item">
              <button
                id="hamburger"
                type="button"
                className={classNames('navbar-toggle', { collapsed: !isMemuOpen })}
                onClick={() => setIsMemuOpen(val => !val)}
              >
                <span className="sr-only">Toggle navigation</span>
                <span className="icon-bar" />
                <span className="icon-bar" />
                <span className="icon-bar" />
              </button>
            </div>
            <div className="item">
              <a
                id="our-showrooms"
                onClick={() => {
                  const isVirtual = store?.location_name === 'Other';
                  trackEventWithObject({
                    event: 'header',
                    general_event_category: isVirtual ? 'Virtual Appointment' : 'Visit Showrooms',
                    general_event_section: 'Main Icon',
                    general_event_details: isVirtual ? 'Undefined' : showroomName || 'Undefined',
                  });
                }}
                href={store?.location_page}
                className="ir234-make-appointment-link"
              >
                <span className="sr-only">{visitText}</span>
                <FontIcon name={store?.location_name === 'Other' ? 'tv' : 'location'} />
                <span className="sr-only">our showrooms</span>
                <span className="hidden-xs-inline visit">{visitText}</span>
              </a>
            </div>
            <div className="item item-logo tw-h-[43px]">
              <div id="site-logo">
                <a href="/" aria-label="Home">
                  <Image
                    width="172"
                    height={13}
                    role="img"
                    alt="Brilliant Earth Logo"
                    src="//css.brilliantearth.com/static/img/logo-new.svg"
                  />
                </a>
              </div>
            </div>
            <div className="item">
              <a
                onClick={() => {
                  (window as any).dataLayer.push({ event: 'mobile_click_to_call' });
                  trackEventWithObject({
                    event: 'header',
                    general_event_category: 'Call Us',
                    general_event_section: 'Main Icon',
                    general_event_details: showroomName || 'Undefined',
                  });
                }}
                id="header-telephone-xs"
                className="phone_click"
                href={`tel:+${phone}`}
                aria-label={`Contact us ${phone}`}
                data-event1-category="Header"
                data-event1-action="Click"
                data-event1-label={`Call Us_${store?.location_name}`}
              >
                <i className="iconfont iconfont-tel1" />
              </a>
            </div>
            <div className="item">
              <a
                onClick={() => {
                  trackEventWithObject({
                    event: 'header',
                    general_event_category: 'My Bag',
                    general_event_section: 'Main Icon',
                    general_event_details: 'Undefined',
                  });
                }}
                id="mybag-toggler"
                href="/shop/cart/"
                aria-label="Shopping Bag"
              >
                <i className="iconfont iconfont-bag" />
              </a>
            </div>
          </div>
        </div>
        <div
          className={classNames('main-search-mobile-ab2', {
            'shrink-bar': isMemuOpen || isTopNavBarHide,
          })}
        >
          <div className="home-search-mobile-wrap" id="search-panel-mobile">
            <div className="wrapper">
              <div className="tw-flex tw-relative">
                <div
                  className={classNames('tw-w-full', {
                    'king-search': isBelowDesktop && !isMemuOpen,
                  })}
                >
                  <form
                    id={isBelowDesktop && !isMemuOpen ? 'king-search-form' : 'mobile-form'}
                    action="/search/"
                    method="get"
                    className="input-group input-group-sm home-search-mobile ng-scope ng-pristine ng-valid"
                  >
                    <input
                      id={isBelowDesktop && !isMemuOpen ? 'king-search-input' : 'mobile-input'}
                      type="text"
                      className="inner-search-input form-control placeholder searchspring-query ng-scope tw-font-book"
                      autoComplete="off"
                      name="q"
                      placeholder="Search"
                      aria-label="Search"
                      spellCheck="false"
                      onClick={() => {
                        trackEventWithObject({
                          event: 'header',
                          general_event_category: 'Search',
                          general_event_section: 'Search Bar Click',
                          general_event_details: 'Undefined',
                        });
                      }}
                    />
                    <span className="input-group-btn">
                      <button
                        type="submit"
                        onClick={e => {
                          const input = (e.target as HTMLButtonElement)
                            .closest('form')
                            ?.querySelector('input.inner-search-input') as HTMLInputElement;

                          trackEventWithObject({
                            event: 'header',
                            general_event_category: 'Search',
                            general_event_section: 'Search Submit',
                            general_event_details: input?.value || 'Undefined',
                          });
                        }}
                        className="btn btn-default iconfont iconfont-search"
                        aria-label="Submit Search"
                      />
                    </span>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <nav
        className={classNames('king-menu-mobile mm-slideout mm-menu mm-border-offset mm-offcanvas', {
          'mm-opened': isMemuOpen,
        })}
        style={navTop !== 0 ? { top: navTop } : {}}
      >
        {/* Match Django mount: #be-mobile-nav-links-root inside .king-menu-mobile */}
        {mobileNavHtml ? (
          <>
            <div id={MOBILE_NAV_ROOT_ID} dangerouslySetInnerHTML={{ __html: mobileNavHtml }} />
            <MobileNavigationIsland isOpen={isMemuOpen && isBelowDesktop} rootId={MOBILE_NAV_ROOT_ID} />
          </>
        ) : (
          <div id={MOBILE_NAV_ROOT_ID}>
            <MobileNavigation isOpen={isMemuOpen && isBelowDesktop} mobileNavLinks={mobileNavLinks} />
          </div>
        )}
      </nav>
      <div
        className={classNames('mm-blocker mm-slideout', { 'tw-block': isMemuOpen })}
        onClick={() => setIsMemuOpen(val => !val)}
        aria-hidden
      />
    </Container>
  );
}
